import { PopoverProps } from "@chakra-ui/react";
import { BookmarkIcon } from "../../icons/BookmarkIcon";
import { FollowIcon } from "../../icons/FollowIcon";
import { EyeBlindIcon } from "../../icons/EyeBlindIcon";
import { EyeIcon } from "../../icons/EyeIcon";
import { useTranslation } from "react-i18next";
import { Comic } from "../../../types";
import { useToggleComicFavorite } from "../../cards/hooks/useToggleComicFavorite";
import { useToggleArtistFollow } from "../../cards/hooks/useToggleArtistFollow";
import { KebabContextualMenu } from "../../common/KebabContextualMenu";
import { ShareMenuItem } from "../../common/ShareMenuItem";
import { useToggleHideFromReadingHistory } from "../hooks/useToggleHideFromReadingHistory";
import { useIsComicHidden } from "../hooks/useIsComicHidden";
import { BookmarkCheckedIcon } from "../../icons/reader/BookmarkCheckedIcon";
import { FollowingIcon } from "../../icons/FollowingIcon";

export interface ComicDetailsMenuProps {
  comic: Comic;
  backgroundColor?: string;
  menuPlacement?: PopoverProps["placement"];
  iconSize?: string;
  buttonSize?: string;
  borderRadius?: string;
  gutter?: number;
  margin?: string;
}

export function ComicDetailsMenu({
  comic,
  backgroundColor = "transparent",
  menuPlacement = "top-end",
  iconSize = "20px",
  buttonSize,
  borderRadius,
  gutter,
  margin,
}: ComicDetailsMenuProps) {
  const { t } = useTranslation();

  const handleToggleFavorite = useToggleComicFavorite(
    `${comic.entity_type}-${comic.id}`,
  );

  const { artistData, handleToggleArtistFollow } = useToggleArtistFollow(
    `${comic.artist.entity_type}-${comic.artist.id}`,
  );

  const { toggleHideFromReadingHistory } = useToggleHideFromReadingHistory();
  const isComicHidden = useIsComicHidden(comic);

  return (
    <KebabContextualMenu
      backgroundColor={backgroundColor}
      menuPlacement={menuPlacement}
      iconSize={iconSize}
      buttonSize={buttonSize}
      borderRadius={borderRadius}
      gutter={gutter}
      margin={margin}
      aria-label="Comic options"
      items={[
        {
          id: "library",
          text: comic.is_favorite
            ? t("components.slideInMenu.comicDetailsMenu.removeFromLibrary")
            : t("components.slideInMenu.comicDetailsMenu.addToLibrary"),
          icon: comic.is_favorite ? (
            <BookmarkCheckedIcon
              boxSize="20px"
              transition="inherit"
              _groupHover={{ color: "blaze.blaze" }}
            />
          ) : (
            <BookmarkIcon
              boxSize="20px"
              transition="inherit"
              _groupHover={{ color: "blaze.blaze" }}
            />
          ),
          onClick: handleToggleFavorite,
        },
        {
          id: "follow",
          text: artistData?.user_is_following
            ? t("components.slideInMenu.comicDetailsMenu.unfollowCreator")
            : t("components.slideInMenu.comicDetailsMenu.followCreator"),
          icon: artistData?.user_is_following ? (
            <FollowingIcon
              boxSize="20px"
              transition="inherit"
              _groupHover={{ color: "blaze.blaze" }}
            />
          ) : (
            <FollowIcon
              boxSize="21px"
              transition="inherit"
              _groupHover={{ color: "blaze.blaze" }}
            />
          ),
          onClick: handleToggleArtistFollow,
        },
        {
          id: "share",
          text: t("components.slideInMenu.comicDetailsMenu.share"),
          icon: <></>, // Not used for custom components
          onClick: () => {}, // Not used for custom components
          customComponent: (
            <ShareMenuItem
              key="share"
              shareUrl={comic.share_url}
              shareTitle={t("components.slideInMenu.comicDetailsMenu.share")}
              isLastItem={false}
              popoverPlacement="top-start"
            />
          ),
        },
        {
          id: "hide",
          text: isComicHidden
            ? t(
                "components.slideInMenu.comicDetailsMenu.hiddenFromReadingHistory",
              )
            : t(
                "components.slideInMenu.comicDetailsMenu.hideFromReadingHistory",
              ),
          icon: isComicHidden ? (
            <EyeBlindIcon
              boxSize="20px"
              transition="inherit"
              _groupHover={{ color: "blaze.blaze" }}
            />
          ) : (
            <EyeIcon
              boxSize="20px"
              transition="inherit"
              _groupHover={{ color: "blaze.blaze" }}
            />
          ),
          onClick: () => toggleHideFromReadingHistory({ comicId: comic.id }),
          isLastItem: true,
        },
      ]}
    />
  );
}
